import { useEffect, useRef, useState } from "react";
import { Compass, Loader2, MousePointerClick } from "lucide-react";

const PANNELLUM_CSS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
const PANNELLUM_JS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";

function loadPannellum() {
  return new Promise((resolve) => {
    if (window.pannellum) return resolve();
    if (!document.querySelector(`link[href="${PANNELLUM_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = PANNELLUM_CSS;
      document.head.appendChild(link);
    }
    if (!document.getElementById("pnlm-custom-style")) {
      const style = document.createElement("style");
      style.id = "pnlm-custom-style";
      style.textContent = `
        .pnlm-hotspot.pnlm-nav-arrow{width:52px;height:52px;margin:-26px 0 0 -26px;border-radius:9999px;
          background:rgba(198,160,74,.92);box-shadow:0 6px 18px rgba(0,0,0,.4);cursor:pointer;
          display:flex;align-items:center;justify-content:center;transition:transform .18s ease, background .18s ease;
          animation:pnlmPulse 2.2s ease-in-out infinite;}
        .pnlm-hotspot.pnlm-nav-arrow:hover{transform:scale(1.15);background:rgba(198,160,74,1);animation:none;}
        .pnlm-hotspot.pnlm-nav-arrow::after{content:"";width:16px;height:16px;border-top:3px solid #112012;
          border-right:3px solid #112012;transform:rotate(45deg);margin-left:-4px;}
        .pnlm-hotspot.pnlm-nav-arrow.pnlm-nav-back::after{transform:rotate(-135deg);margin-left:4px;}
        @keyframes pnlmPulse{0%,100%{box-shadow:0 6px 18px rgba(0,0,0,.4),0 0 0 0 rgba(198,160,74,.5);}
          50%{box-shadow:0 6px 18px rgba(0,0,0,.4),0 0 0 12px rgba(198,160,74,0);}}

        /* On-photo navigation bar (works in fullscreen because it lives inside the pannellum container) */
        .pnlm-navbar{position:absolute;left:50%;bottom:18px;transform:translateX(-50%);z-index:100;
          display:flex;align-items:center;gap:8px;padding:6px;border-radius:9999px;
          background:rgba(17,32,18,.72);backdrop-filter:blur(6px);box-shadow:0 6px 20px rgba(0,0,0,.35);}
        .pnlm-navbtn{border:0;cursor:pointer;color:#112012;background:#c6a04a;font-weight:600;
          border-radius:9999px;padding:8px 16px;font-size:14px;display:flex;align-items:center;gap:6px;
          transition:background .18s ease, transform .18s ease;font-family:inherit;line-height:1;}
        .pnlm-navbtn:hover{background:#d8b45f;transform:translateY(-1px);}
        .pnlm-navbtn.pnlm-navicon{padding:8px 12px;background:rgba(255,255,255,.9);color:#112012;}
        .pnlm-navbtn.pnlm-navicon:hover{background:#fff;}
        .pnlm-navlabel{color:#f5efe6;font-size:13px;font-weight:600;padding:0 4px;white-space:nowrap;
          font-family:inherit;letter-spacing:.02em;}
      `;
      document.head.appendChild(style);
    }
    const existing = document.querySelector(`script[src="${PANNELLUM_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      if (window.pannellum) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = PANNELLUM_JS;
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
}

export default function Panorama360({ images = [] }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadPannellum().then(() => { if (mounted) setReady(true); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!ready || !images.length || !containerRef.current || !window.pannellum) return;
    if (viewerRef.current) {
      try { viewerRef.current.destroy(); } catch (e) {}
      viewerRef.current = null;
    }

    const total = images.length;
    const scenes = {};
    images.forEach((url, i) => {
      const hotSpots = [];
      if (total > 1) {
        const next = (i + 1) % total;
        const prev = (i - 1 + total) % total;
        hotSpots.push({ pitch: -12, yaw: 0, type: "scene", sceneId: `scene${next}`,
          cssClass: "pnlm-nav-arrow", text: `Vai alla vista ${next + 1}` });
        hotSpots.push({ pitch: -12, yaw: 180, type: "scene", sceneId: `scene${prev}`,
          cssClass: "pnlm-nav-arrow pnlm-nav-back", text: `Vai alla vista ${prev + 1}` });
      }
      scenes[`scene${i}`] = { type: "equirectangular", panorama: url, hotSpots };
    });

    const viewer = window.pannellum.viewer(containerRef.current, {
      default: {
        firstScene: "scene0",
        autoLoad: true,
        showControls: true,
        autoRotate: -2,
        autoRotateInactivityDelay: 3000,
        compass: true,
        sceneFadeDuration: 900,
      },
      scenes,
    });
    viewerRef.current = viewer;

    // On-photo navigation bar (appended INSIDE the pannellum container so it stays in fullscreen)
    let navbar = null;
    let labelEl = null;
    const updateLabel = () => {
      if (!labelEl) return;
      const cur = parseInt(String(viewer.getScene()).replace("scene", ""), 10) || 0;
      labelEl.textContent = `Vista ${cur + 1} / ${total}`;
    };
    const go = (dir) => {
      const cur = parseInt(String(viewer.getScene()).replace("scene", ""), 10) || 0;
      const target = ((dir === "next" ? cur + 1 : cur - 1) + total) % total;
      try { viewer.loadScene(`scene${target}`); } catch (e) {}
    };
    if (total > 1) {
      navbar = document.createElement("div");
      navbar.className = "pnlm-navbar";
      navbar.setAttribute("data-testid", "panorama-navbar");

      const prevBtn = document.createElement("button");
      prevBtn.className = "pnlm-navbtn pnlm-navicon";
      prevBtn.setAttribute("data-testid", "panorama-prev-btn");
      prevBtn.setAttribute("aria-label", "Vista precedente");
      prevBtn.textContent = "‹";
      prevBtn.onclick = (e) => { e.stopPropagation(); go("prev"); };

      labelEl = document.createElement("span");
      labelEl.className = "pnlm-navlabel";

      const nextBtn = document.createElement("button");
      nextBtn.className = "pnlm-navbtn";
      nextBtn.setAttribute("data-testid", "panorama-next-btn");
      nextBtn.setAttribute("aria-label", "Vista successiva");
      nextBtn.textContent = "Vista successiva ›";
      nextBtn.onclick = (e) => { e.stopPropagation(); go("next"); };

      navbar.appendChild(prevBtn);
      navbar.appendChild(labelEl);
      navbar.appendChild(nextBtn);
      containerRef.current.appendChild(navbar);
      updateLabel();
    }

    viewer.on("scenechange", updateLabel);

    return () => {
      try { if (navbar && navbar.parentNode) navbar.parentNode.removeChild(navbar); } catch (e) {}
      if (viewerRef.current) { try { viewerRef.current.destroy(); } catch (e) {} viewerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, images]);

  if (!images.length) return null;

  return (
    <div data-testid="panorama-360">
      <h3 className="font-display text-2xl text-fir mb-2 flex items-center gap-2">
        <Compass size={20} className="text-gold" /> Tour virtuale 360°
      </h3>
      <p className="text-sm text-fir-deep/60 mb-4 flex items-center gap-1.5">
        <MousePointerClick size={15} className="text-gold" />
        Trascina per guardarti intorno · usa i comandi sulla foto per passare da una stanza all'altra (anche a schermo intero)
      </p>
      <div className="relative rounded-3xl overflow-hidden border border-wood/30 bg-fir-deep">
        {!ready && (
          <div className="h-[460px] flex items-center justify-center text-ivory/70">
            <Loader2 className="animate-spin mr-2" /> Caricamento tour…
          </div>
        )}
        <div ref={containerRef} style={{ width: "100%", height: ready ? 460 : 0 }} />
      </div>
    </div>
  );
}
